import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import NodeMarkerOffline from '@page/Maps/components/icon/gswm-marker-offline.svg';
import NodeMarkerAlarm from '@page/Maps/components/icon/gswm-marker-alarm.svg';
import { Button, Typography, Spin } from 'antd';
import { ReactComponent as LocationSvg } from '@page/Maps/components/icon/Location.svg';
import {
  RightOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import _ from 'lodash';
import { formatCurrency } from '@utils/utils';
import { STREET_BASEMAP } from '@components/map/basemaps';
import { apiGetDevicesForMap } from '@services/ManageDevices/DeviceServices';
import { showError } from '@components/notification';

const DashboardMap = () => {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [currentZoom, setCurrentZoom] = useState(12);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await apiGetDevicesForMap();
      const convertData = res?.data?.data?.length
        ? res?.data?.data.map((device) => ({
            id: device?.id || '',
            device_id: device?.id || '',
            name: device?.customer_name || '',
            code: device?.customer_id || '',
            address: device?.address || '',
            lat: device?.latitude || '',
            lng: device?.longitude || '',
            serial_no: device?.serial_no,
            model: device?.model,
            meter_reading: device?.meter_reading,
            status: device?.status,
            last_seen_at: device?.last_seen_at,
            rsrp: device?.rsrp,
            snr: device?.snr,
            battery_capacity: device?.battery_capacity,
            alarm_status: device?.alarm_status
          }))
        : [];
      setDevices(convertData);
    } catch (e) {
      console.error('Error fetching map data:', e);
      showError('Không thể tải dữ liệu bản đồ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const createDynamicIcon = (zoom, marker) => {
    const baseSize = 40;
    const scaleFactor = Math.max(0.5, Math.min(2, zoom / 10));
    const size = baseSize * scaleFactor;

    // Ưu tiên hiển thị icon alarm, sau đó offline
    let iconUrl = !_.isEmpty(marker?.alarm_status) ? NodeMarkerAlarm : NodeMarkerOffline;

    return new L.Icon({
      iconUrl: iconUrl,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size]
    });
  };

  const MapEvents = () => {
    const map = useMapEvents({
      zoomend: () => {
        setCurrentZoom(map.getZoom());
      }
    });
    return null;
  };

  const handleDirectionsClick = (lat, lng) => {
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  if (loading) {
    return (
      <div
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Spin />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '400px',
        overflow: 'hidden'
      }}>
      <MapContainer
        center={[20.893071577825484, 106.5946515564822]}
        zoom={12}
        className="dash-map"
        style={{ width: '100%', height: '100%', borderRadius: 8 }}>
        <TileLayer
          attribution={STREET_BASEMAP.attribution}
          url={STREET_BASEMAP.url}
          maxNativeZoom={STREET_BASEMAP.maxNativeZoom}
          maxZoom={STREET_BASEMAP.maxZoom}
        />
        <MapEvents />
        {devices?.map((marker) => (
          <Marker
            position={[marker.lat, marker.lng]}
            key={marker.id}
            icon={createDynamicIcon(currentZoom, marker)}>
            <Popup>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  minWidth: '250px'
                }}>
                <Typography.Text style={{ fontSize: '16px', fontWeight: '500' }}>
                  {marker?.code} - {marker?.name}
                </Typography.Text>
                {/* Online/Offline Status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {marker?.status === 'online' ? (
                    <CheckCircleOutlined style={{ fontSize: '20px', color: '#10b981' }} />
                  ) : (
                    <CloseCircleOutlined style={{ fontSize: '20px', color: '#e11d48' }} />
                  )}
                  <Typography.Text
                    style={{
                      fontSize: '14px',
                      color: marker?.status === 'online' ? '#10b981' : '#e11d48'
                    }}>
                    {marker?.status === 'online'
                      ? 'Online'
                      : marker?.status === 'offline'
                      ? 'Offline'
                      : 'Chưa lắp đặt'}
                  </Typography.Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <LocationSvg style={{ marginRight: '4px' }} />
                  <Typography.Text style={{ fontSize: '14px', color: '#52525B' }}>
                    {`${
                      marker?.address?.length > 35
                        ? `${marker?.address?.slice(0, 35)}...`
                        : marker?.address
                    }`}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text style={{ fontSize: '14px' }}>
                    Số sêri: {marker?.serial_no ?? '-'}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text style={{ fontSize: '14px' }}>
                    Chỉ số: {marker?.meter_reading ? formatCurrency(marker?.meter_reading, 3) : '-'}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text style={{ fontSize: '14px' }}>
                    Lần truyền cuối:{' '}
                    {marker?.last_seen_at
                      ? new Intl.DateTimeFormat('vi-VN', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        }).format(marker.last_seen_at)
                      : '-'}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text style={{ fontSize: '14px' }}>
                    Cảnh báo: {_.isEmpty(marker?.alarm_status) ? 'Không có' : marker?.alarm_status}
                  </Typography.Text>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    width: '100%',
                    justifyContent: 'center'
                  }}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<InfoCircleOutlined />}
                    style={{
                      backgroundColor: '#0284c7',
                      color: '#fff',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                    onClick={() => window.open(`/device/detail/${marker.device_id}`, '_blank')}>
                    Chi tiết
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    icon={<RightOutlined />}
                    style={{
                      backgroundColor: '#1A73E8',
                      color: '#fff',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                    onClick={() => handleDirectionsClick(marker.lat, marker.lng)}>
                    Chỉ đường
                  </Button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default DashboardMap;
